/**
 * Wine By the Case / Red Box
 * Live store status, 90 minute countdown ticker, Shabbos + Yom Tov aware.
 *
 * HEBCAL INTEGRATION
 * ------------------
 * Endpoint : https://www.hebcal.com/hebcal?cfg=json&maj=on&c=on&M=on&geo=zip&zip=11225&year=now&b=18
 *            Whole calendar year of major-holiday + Shabbos candle-lighting + havdalah for
 *            ZIP 11225 (Crown Heights, Brooklyn).
 * We use   : every "candles" and "havdalah" item; each dated ISO 8601 with the NY offset.
 *            Sorted chronologically, pairs of candles->havdalah define closed intervals
 *            (Shabbos, single-day Yom Tov, 2-day, or 3-day chagim all fall out uniformly).
 * Cache key: localStorage["wbtc_hebcal_v2"] = { ts, intervals: [{start,end,name}] }
 * TTL      : 6 hours. Fresh cache = zero network requests.
 * Fallback : if fetch fails and no cache, revert to weekday config only + a conservative
 *            Friday early close.
 *
 * The store never opens Saturday. Yom Tov intervals may span weekdays; those weekdays
 * are marked closed with a reason ("Closed for Rosh Hashana"), or open/close hours are
 * chopped when a Yom Tov begins or ends mid-day.
 */
(function () {
  'use strict';

  var HEBCAL_URL = 'https://www.hebcal.com/hebcal?cfg=json&maj=on&mod=off&mf=off&ss=off&nx=off&c=on&M=on&geo=zip&zip=11225&year=now&b=18';
  var CACHE_KEY = 'wbtc_hebcal_v2';
  var CACHE_TTL_MS = 6 * 60 * 60 * 1000;
  var COUNTDOWN_WINDOW_MS = 90 * 60 * 1000;
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /* ------------------------------------------------------------------ *
   *  New York wall-time helpers (unchanged)
   * ------------------------------------------------------------------ */
  var nyPartsFmt = null, nyTimeFmt = null;

  function buildFormatters() {
    try {
      nyPartsFmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: false
      });
      nyTimeFmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true
      });
      return typeof nyPartsFmt.formatToParts === 'function';
    } catch (err) { return false; }
  }

  function nyParts(date) {
    var parts = nyPartsFmt.formatToParts(date), out = {};
    for (var i = 0; i < parts.length; i++) if (parts[i].type !== 'literal') out[parts[i].type] = parts[i].value;
    return {
      year: parseInt(out.year, 10), month: parseInt(out.month, 10), day: parseInt(out.day, 10),
      hour: parseInt(out.hour, 10) % 24, minute: parseInt(out.minute, 10), second: parseInt(out.second, 10)
    };
  }

  function nyWallToEpoch(y, mo, d, h, mi) {
    var target = Date.UTC(y, mo - 1, d, h, mi, 0), guess = target;
    for (var i = 0; i < 2; i++) {
      var p = nyParts(new Date(guess));
      var asIf = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
      guess += target - asIf;
    }
    return guess;
  }

  function fmtTime(epoch) { return nyTimeFmt.format(new Date(epoch)); }
  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  function fmtCountdown(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    var h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
    return h + ':' + pad2(m) + ':' + pad2(s);
  }

  function parseHM(str, fallback) {
    var m = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(str || '');
    if (!m) m = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(fallback);
    return { h: parseInt(m[1], 10) % 24, m: parseInt(m[2], 10) % 60 };
  }

  /* ------------------------------------------------------------------ *
   *  Hebcal fetch, cache, and interval building
   * ------------------------------------------------------------------ */
  function readCache() {
    try {
      var raw = window.localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      if (d && typeof d.ts === 'number' && d.intervals && d.intervals.length) return d;
    } catch (err) {}
    return null;
  }

  function writeCache(intervals) {
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), intervals: intervals }));
    } catch (err) {}
  }

  /* Turn a raw Hebcal response into a sorted array of {start, end, name} closed intervals.
     Each pair of (candles, next havdalah) becomes one interval. 3-day chagim have multiple
     candle-lighting rows in a row — we open on the first and close on the trailing havdalah. */
  function buildIntervals(json) {
    var items = (json && json.items) || [];
    var events = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it || typeof it.date !== 'string') continue;
      if (it.category !== 'candles' && it.category !== 'havdalah') continue;
      var epoch = Date.parse(it.date);
      if (isNaN(epoch)) continue;
      events.push({ epoch: epoch, kind: it.category, memo: it.memo || '' });
    }
    events.sort(function (a, b) { return a.epoch - b.epoch; });

    var intervals = [], open = null, name = null;
    for (var j = 0; j < events.length; j++) {
      var e = events[j];
      if (e.kind === 'candles') {
        if (open === null) { open = e.epoch; name = cleanName(e.memo); }
      } else {
        if (open !== null) {
          intervals.push({ start: open, end: e.epoch, name: name });
          open = null; name = null;
        }
      }
    }
    return intervals;
  }

  /* Strip "Erev ", trailing " 5787", roman-numeral day markers. Empty memo = Shabbos. */
  function cleanName(memo) {
    if (!memo) return 'Shabbos';
    var n = memo.replace(/^Erev\s+/, '').replace(/\s+5\d{3}$/, '').replace(/\s+(I|II|III)$/, '');
    return n || 'Shabbos';
  }

  function fetchIntervals() {
    return window.fetch(HEBCAL_URL).then(function (res) {
      if (!res.ok) throw new Error('hebcal http ' + res.status);
      return res.json();
    }).then(buildIntervals);
  }

  function getIntervals(cb) {
    var cache = readCache();
    if (cache && Date.now() - cache.ts < CACHE_TTL_MS) { cb(cache.intervals); return; }
    if (!window.fetch) { cb(cache ? cache.intervals : []); return; }
    fetchIntervals().then(function (arr) {
      if (arr && arr.length) writeCache(arr);
      cb(arr);
    }).catch(function () { cb(cache ? cache.intervals : []); });
  }

  /* Find the interval that contains a moment, if any. */
  function intervalAt(intervals, epoch) {
    for (var i = 0; i < intervals.length; i++) {
      var v = intervals[i];
      if (epoch >= v.start && epoch < v.end) return v;
    }
    return null;
  }

  /* Chop a [open, close] weekday window against all intervals. If the whole window falls
     inside an interval → return null (day is closed all day, reason attached). If the
     interval clips only the tail → shorten close. Only the head → move open. Both → null. */
  function chopAgainstIntervals(open, close, intervals) {
    if (open === null || close === null) return { open: open, close: close, reason: null };
    for (var i = 0; i < intervals.length; i++) {
      var v = intervals[i];
      if (v.end <= open || v.start >= close) continue;               // no overlap
      var headClip = v.start > open && v.start < close;              // interval clips tail
      var tailClip = v.end > open && v.end < close;                  // interval clips head
      if (headClip && tailClip) return { open: open, close: close, reason: null }; // interval strictly inside — extremely rare, leave alone
      if (headClip && !tailClip) { close = v.start; }                // Yom Tov starts mid-day
      else if (tailClip && !headClip) { open = v.end; }              // Yom Tov ends mid-day
      else { return { open: null, close: null, reason: v.name }; }   // full-day closed
    }
    if (close <= open) return { open: null, close: null, reason: null };
    return { open: open, close: close, reason: null };
  }

  /* ------------------------------------------------------------------ *
   *  Weekly schedule
   * ------------------------------------------------------------------ */
  function buildSchedule(cfg, intervals, nowP) {
    var days = [];
    for (var i = 0; i <= 8; i++) {
      var wall = new Date(Date.UTC(nowP.year, nowP.month - 1, nowP.day + i));
      var y = wall.getUTCFullYear(), mo = wall.getUTCMonth() + 1, d = wall.getUTCDate(), dow = wall.getUTCDay();
      var entry = { dow: dow, y: y, mo: mo, d: d, open: null, close: null, reason: null };

      if (dow >= 0 && dow <= 4) {
        entry.open = nyWallToEpoch(y, mo, d, cfg.open.h, cfg.open.m);
        var c = dow === 4 ? cfg.closeThu : cfg.closeSunWed;
        entry.close = nyWallToEpoch(y, mo, d, c.h, c.m);
      } else if (dow === 5) {
        entry.open = nyWallToEpoch(y, mo, d, cfg.openFri.h, cfg.openFri.m);
        // Prefer today's candle-lighting from Hebcal; only fall back to the
        // hard-coded friFallback when no interval starts today. friFallback
        // is already an absolute close time, so offsetMin does NOT apply to it.
        var dayStart = nyWallToEpoch(y, mo, d, 0, 0);
        var dayEnd = dayStart + 86400000;
        var candles = null;
        for (var k = 0; k < intervals.length; k++) {
          if (intervals[k].start >= dayStart && intervals[k].start < dayEnd) {
            candles = intervals[k].start; break;
          }
        }
        if (candles !== null) {
          entry.close = candles - cfg.offsetMin * 60000;
        } else {
          entry.close = nyWallToEpoch(y, mo, d, cfg.friFallback.h, cfg.friFallback.m);
        }
        if (entry.close <= entry.open) { entry.open = null; entry.close = null; }
      }
      // Saturday stays fully closed by default.

      var chopped = chopAgainstIntervals(entry.open, entry.close, intervals);
      entry.open = chopped.open; entry.close = chopped.close; entry.reason = chopped.reason;

      if (entry.open !== null && entry.close !== null && entry.close <= entry.open) {
        entry.open = null; entry.close = null;
      }
      days.push(entry);
    }
    return days;
  }

  function computeState(days, now) {
    for (var i = 0; i < days.length; i++) {
      var d = days[i];
      if (d.open === null) {
        // Fully closed day. If it's today, expose the reason via a synthetic 'closed-today' slot
        // so the outer state loop still finds a "nextAt" (next opening).
        continue;
      }
      if (now >= d.open && now < d.close) return { open: true, nextAt: d.close, day: d };
      if (now < d.open) return { open: false, nextAt: d.open, day: d };
    }
    return null;
  }

  /* If today is fully closed and it's inside an interval, surface the reason on the label. */
  function closureReasonNow(intervals, now) {
    var v = intervalAt(intervals, now);
    return v ? v.name : null;
  }

  function relDayName(day, nowP) {
    if (day.y === nowP.year && day.mo === nowP.month && day.d === nowP.day) return 'today';
    var t = new Date(Date.UTC(nowP.year, nowP.month - 1, nowP.day + 1));
    if (day.y === t.getUTCFullYear() && day.mo === t.getUTCMonth() + 1 && day.d === t.getUTCDate()) return 'tomorrow';
    return DAY_NAMES[day.dow];
  }

  /* ------------------------------------------------------------------ *
   *  Per-section wiring
   * ------------------------------------------------------------------ */
  function initHoursPopover(root) {
    var wrap = root.querySelector('[data-hours]');
    var toggle = root.querySelector('[data-hours-toggle]');
    if (!wrap || !toggle) return;
    function setOpen(open) {
      wrap.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    toggle.addEventListener('click', function () { setOpen(!wrap.classList.contains('is-open')); });
    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) setOpen(false); });
    wrap.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && wrap.classList.contains('is-open')) { setOpen(false); toggle.focus(); }
    });
    var canHover = false;
    try { canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch (err) {}
    if (canHover) {
      wrap.addEventListener('mouseenter', function () { setOpen(true); });
      wrap.addEventListener('mouseleave', function () { setOpen(false); });
    }
  }

  function initSection(root) {
    var cfgEl = root.querySelector('[data-redbox-config]');
    var statusEl = root.querySelector('[data-status-line]');
    var tickerWrap = root.querySelector('[data-ticker]');
    var tickerDigits = root.querySelector('[data-ticker-digits]');
    var contextEl = root.querySelector('[data-context]');
    var srEl = root.querySelector('[data-sr-status]');
    if (!cfgEl || !statusEl || !tickerWrap || !tickerDigits || !contextEl || !srEl) return;

    var raw = {};
    try { raw = JSON.parse(cfgEl.textContent) || {}; } catch (err) { raw = {}; }
    var offsetMin = typeof raw.friCloseOffsetMin === 'number' ? raw.friCloseOffsetMin : 30;
    var cfg = {
      open: parseHM(raw.openSunThu, '10:00'),
      closeSunWed: parseHM(raw.closeSunWed, '22:00'),
      closeThu: parseHM(raw.closeThu, '22:00'),
      openFri: parseHM(raw.openFri, '08:00'),
      offsetMin: Math.min(Math.max(offsetMin, 0), 180),
      friFallback: parseHM(raw.friFallbackClose, '15:30')
    };

    initHoursPopover(root);

    var state = { mode: null, ctx: null, digits: null, intervals: [], schedule: null, scheduleKey: null };

    function markToday(nowP) {
      var wall = new Date(Date.UTC(nowP.year, nowP.month - 1, nowP.day));
      var todayDow = String(wall.getUTCDay());
      var rows = root.querySelectorAll('[data-day]');
      for (var i = 0; i < rows.length; i++) {
        rows[i].classList.toggle('is-today', rows[i].getAttribute('data-day') === todayDow);
      }
    }

    function updateFridayRow() {
      var cell = root.querySelector('[data-fri-close]');
      if (!cell || !state.schedule) return;
      for (var i = 0; i < state.schedule.length; i++) {
        var d = state.schedule[i];
        if (d.dow === 5 && d.close !== null) {
          var text = fmtTime(d.close);
          if (cell.textContent !== text) cell.textContent = text;
          return;
        }
      }
    }

    function ensureSchedule(force) {
      var nowP = nyParts(new Date());
      var key = nowP.year + '-' + nowP.month + '-' + nowP.day;
      if (force || !state.schedule || state.scheduleKey !== key) {
        state.schedule = buildSchedule(cfg, state.intervals, nowP);
        state.scheduleKey = key;
        markToday(nowP);
        updateFridayRow();
      }
      return nowP;
    }

    function headlineFor(mode, reason) {
      if (mode === 'closing') return 'Closing in';
      if (mode === 'opening') return 'Opening in';
      if (mode === 'open') return 'Open now';
      if (reason) return 'Closed for ' + reason;
      return 'Closed';
    }

    function contextFor(mode, st, nowP, reason) {
      var day = st.day;
      if (mode === 'closing' || mode === 'opening') {
        return DAY_NAMES[day.dow] + ' hours: ' + fmtTime(day.open) + ' to ' + fmtTime(day.close);
      }
      if (mode === 'open') return 'Closes today ' + fmtTime(day.close);
      return 'Opens ' + relDayName(day, nowP) + ' ' + fmtTime(day.open);
    }

    function srFor(mode, ctx, reason) {
      if (mode === 'closing') return 'The shop is open and closes soon. ' + ctx + '.';
      if (mode === 'opening') return 'The shop is closed and opens soon. ' + ctx + '.';
      if (mode === 'open') return 'The shop is open now. ' + ctx + '.';
      if (reason) return 'The shop is closed for ' + reason + '. ' + ctx + '.';
      return 'The shop is closed. ' + ctx + '.';
    }

    var timer = null;

    function tick() {
      if (!document.body.contains(root)) { if (timer) window.clearInterval(timer); return; }

      var nowP = ensureSchedule(false);
      var now = Date.now();
      var st = computeState(state.schedule, now);
      if (!st) return;

      if (now >= st.nextAt) {
        nowP = ensureSchedule(true);
        st = computeState(state.schedule, now);
        if (!st) return;
      }

      var reason = st.open ? null : closureReasonNow(state.intervals, now);
      var remaining = st.nextAt - now;
      var mode;
      if (remaining <= COUNTDOWN_WINDOW_MS && !reason) {
        // Don't show a 90-min countdown to Yom Tov reopening — it may be days away.
        mode = st.open ? 'closing' : 'opening';
      } else {
        mode = st.open ? 'open' : 'closed';
      }

      var inCountdown = mode === 'closing' || mode === 'opening';
      if (inCountdown) {
        var digits = fmtCountdown(remaining);
        if (digits !== state.digits) { state.digits = digits; tickerDigits.textContent = digits; }
        if (tickerWrap.hidden) tickerWrap.hidden = false;
      } else if (!tickerWrap.hidden) { tickerWrap.hidden = true; }

      var ctx = contextFor(mode, st, nowP, reason);
      if (ctx !== state.ctx) { state.ctx = ctx; contextEl.textContent = ctx; }

      var newMode = mode + (reason ? ':' + reason : '');
      if (newMode !== state.mode) {
        state.mode = newMode;
        root.setAttribute('data-mode', mode);
        statusEl.textContent = headlineFor(mode, reason);
        var sr = srFor(mode, ctx, reason);
        if (srEl.textContent !== sr) srEl.textContent = sr;
      }
    }

    ensureSchedule(true);
    tick();
    timer = window.setInterval(tick, 1000);

    getIntervals(function (intervals) {
      if (!intervals || !intervals.length) return;
      state.intervals = intervals;
      ensureSchedule(true);
      state.ctx = null; state.digits = null; state.mode = null;
      tick();
    });
  }

  function initAll() {
    if (!buildFormatters()) return;
    var roots = document.querySelectorAll('[data-redbox]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.getAttribute('data-redbox-init') === '1') continue;
      root.setAttribute('data-redbox-init', '1');
      initSection(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
